import { Injectable, Injector } from '@angular/core';
import { removeFromArray } from '@reflact/tsutil';
import { getParamsForEventHook } from '../iframe-communication/EventHoolParamsHelper';
import { InactiveBotServiceService } from '@shared-angular/services/inactive-bot-service.service';
import type { KeyValAdminService } from '../key-val-admin.service';
import { RagLoggerService } from '@shared-angular/services/rag-logger/rag-logger.service';
import { Subject } from 'rxjs';

type EventHookType = (eventName: string, ...args: unknown[]) => void;
type RegisteredHookType = { id: string, fn: EventHookType, hook: string };
@Injectable({ providedIn: 'root' })

export class EventManagerAdminService {
  public registeredHooks: RegisteredHookType[] = [];
  public _disabled: Record<string, boolean> = {};
  public readonly eventFired$ = new Subject<{ hook: string; params: unknown }>();
  constructor(
    private logger: RagLoggerService,
    public inActiveService: InactiveBotServiceService,
    private injector: Injector
  ) { }

  private _keyValService: KeyValAdminService | null = null;
  private get keyValService(): KeyValAdminService {
    if (!this._keyValService) {
      // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-unsafe-assignment
      const { KeyValAdminService } = require('../key-val-admin.service');
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-non-null-assertion
      this._keyValService = this.injector.get(KeyValAdminService)!;
    }
    return this._keyValService;
  }

  public listenTo(hook: string, fn: EventHookType, id: string = crypto.randomUUID()) {
    hook = hook.split("window.RAGAI.").join("RAGAI.");
    this.registeredHooks.push({ id, fn, hook });
    this._trigger_RAGAI_EVENT("RAGAI.EVENTS.LISTENTO", { hook, id });
    return id;
  }

  public on(hook: string, fn: EventHookType, id: string = crypto.randomUUID()) {
    return this.listenTo(hook, fn, id);
  }

  public off(id: string) {
    this.removeEventHook(id);
  }


  public removeEventHook(id: string) {
    removeFromArray(this.registeredHooks, (e => e.id == id));
  }

  public addEventHook(id: string, fn: EventHookType, hook: string = "*") {
    this.registeredHooks.push({ id, fn, hook });
  }
  /*
    public addEventHookStateUpdate(id: string, fn: EventHookType) {
      this.registeredHooks.push({ id, fn, hook: "io_in_stateUpdate" });
    }

    public addEventHookRunUpdate(id: string, fn: EventHookType) {
      this.registeredHooks.push({ id, fn, hook: "io_in_runUpdate" });
    }

    public addEventHookSkillUsed(id: string, fn: EventHookType) {
      this.registeredHooks.push({ id, fn, hook: "io_in_skillUsed" });
    } */
  /*
    public addEventHookRunComplete(id: string, fn: EventHookType) {
      this.registeredHooks.push({ id, fn, hook: "runComplete" });
    }
    */

  public addEventHookAddUserMessage(id: string, fn: EventHookType) {
    this.registeredHooks.push({ id, fn, hook: "io_in_addUserMessage" });
  }

  public triggerEventHook(hook: string, params: unknown) {
    hook = hook.split("window.RAGAI.").join("RAGAI.");

    this.logger.measureHook(hook, params);

    this.registeredHooks.forEach((rh) => {
      try {
        if (hook == rh.hook) {
          rh.fn(hook, getParamsForEventHook(hook, params));
          return;
        }
        // Nur bei registriertem Wildcard-Hook ("*") als Präfix werten, sonst matchen z.B. "X.ANSWER" auch "X.ANSWER.correct"
        if (rh.hook.includes("*") && hook.startsWith(rh.hook.split("*")[0] ?? "")) {
          rh.fn(hook, getParamsForEventHook(hook, params));
        }
      } catch (e) {
        console.warn(`Error on exec Eventhook with id ${rh.id} check you js`, e);
      }
    });
    this.eventFired$.next({ hook, params });
  }

  public inActiveHook(seconds: number) {
    return this.inActiveService.get(seconds);
  }



  private _callerInfo(instance: unknown): string {
    if (!instance) return "";

    const stack = new Error().stack?.split('\n') ?? [];
    //console.log("Stack:", stack);
    // Gehe von hinten durch den Stack (also vom Ursprung des Aufrufs aus)

    for (let i = stack.length - 1; i >= 0; i--) {
      // @ts-expect-error
      const line = stack[i].trim();
      if (line.includes("<anonymous>")) {
        let found = stack[i - 1];
        found = found?.trimStart();
        found = found?.substring(3, found.length - 1);
        found = found?.trimStart();
        found = found?.split(" ")[0];
        found = found?.trim();
        return found ?? "";
      }
    }
    return "";
  }

  public trigger(name: string, params: unknown = {}) {
    this.triggerEventHook(name, params);
  }

  public _trigger_RAGAI_EVENT(instance: unknown, eventParams: unknown = {}, additionalEventInfo: string = ""): string {
    let returnStr = "";
    if (typeof instance === "string") {
      let eventName = instance;
      if (additionalEventInfo !== "") {
        eventName += `.${additionalEventInfo}`;
      }
      this.triggerEventHook(eventName, eventParams);
      returnStr = eventName;
      return returnStr;
    }
    const managerCallName = this._callerInfo(this);

    const serviceName = managerCallName.split(".")[0];
    const method = managerCallName.split(".")[1];
    //@ts-expect-error

    Object.entries(window.RAGAI).forEach(([key, _value]) => {
      //@ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const cName: string = window.RAGAI[key].constructor.name;
      if (
        serviceName === cName &&
        cName.startsWith("_") &&
        /^[A-Z0-9_]+$/.test(key)) {
        let eventName = "RAGAI." + key + "." + method?.toUpperCase();
        //        console.log(eventName);
        if (additionalEventInfo !== "") {
          eventName += `.${additionalEventInfo}`;
        }


        this.triggerEventHook(eventName, eventParams);
        returnStr = eventName;
        return eventName;

        //console.log("Value:", value);
      }
    });
    return returnStr;
  }

  public async manage(action: 'disable' | 'enable' | 'remove', eventId: string): Promise<void> {
    if (action === 'disable') {
      this._disabled[eventId] = true;
      await this.keyValService.storeKeyValEntry("evt_disabled_" + eventId, true, "EVENT-MANAGER");
    } else if (action === 'enable') {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this._disabled[eventId];
      await this.keyValService.deleteKeyValEntry("evt_disabled_" + eventId, "EVENT-MANAGER");
    } else {
      this.off(eventId);
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this._disabled[eventId];
      await this.keyValService.deleteKeyValEntry("evt_disabled_" + eventId, "EVENT-MANAGER");
    }
  }
}

